<script
  setup
  lang="ts"
  generic="
    ListingFilter extends {
      code: string;
      label: string;
      name: string;
      options: Array<Schemas['PropertyGroupOption'] & { count: number }>;
      entities: Array<Schemas['ProductManufacturer'] & { count: number }>;
    }
  "
>
import { getTranslatedProperty } from "@shopware/helpers";
import { inject, ref } from "vue";
import type { Schemas } from "#shopware";

const props = defineProps<{
  filter: ListingFilter;
}>();

const emits =
  defineEmits<
    (e: "select-value", value: { code: string; value: unknown }) => void
  >();
const selectedOptionIds = inject<string[]>("selectedOptionIds");
const isFilterVisible = ref<boolean>(false);
const toggle = () => {
  isFilterVisible.value = !isFilterVisible.value;
};
</script>

<template>
  <div class="item border-t-0 border-b-2 border-b-cust_green">
    <button
      type="button"
      class="accordion-header w-full text-left py-4 px-3 font-bold flex justify-between items-center cursor-pointer bg-transparent"
      @click="toggle"
    >
      <h6
        class="text-22 font-extrabold font-redit_sans_con tracking-[0.22px] capitalize"
      >
        <template v-if="props.filter.label === 'manufacturer'">
          {{ $t("listing.manufacturer") }}
        </template>
        <template v-else>
          {{ props.filter.label }}
        </template>
      </h6>

      <svg
        width="17"
        height="14"
        viewBox="0 0 16 9"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        class="transform transition-transform duration-200"
        :class="[!isFilterVisible ? 'rotate-0' : 'rotate-180']"
      >
        <path
          d="M15 1L7.99998 7L0.999983 1"
          stroke="#5C6566"
          stroke-width="2"
          stroke-linecap="round"
        />
      </svg>
    </button>
    <transition name="fade" mode="out-in">
      <div
        v-show="isFilterVisible"
        :id="props.filter.code"
        class="item border-t border-t-cust_gray px-3"
      >
        <ul>
          <li
            v-for="option in props.filter.options || props.filter.entities"
            :key="`${option.id}-${selectedOptionIds?.includes(option.id)}`"
            class="flex items-center gap-[10px]"
          >
            <input
              :id="`filter-mobile-${props.filter.code}-${option.id}`"
              :checked="selectedOptionIds?.includes(option.id)"
              :name="props.filter.name"
              :value="option.name"
              :aria-label="`${option.name} filter`"
              type="checkbox"
              class="h-4 w-4 border-secondary-300 rounded text-indigo-600 focus:ring-indigo-500"
              @change="
                emits('select-value', {
                  code: props.filter.code,
                  value: option.id,
                })
              "
            />

            <div v-if="option.media?.url">
              <img
                loading="lazy"
                class="ml-2 h-4 w-4"
                :src="option.media.url"
                :alt="option.media.translated.alt || ''"
                :class="{
                  'border-blue border-2': selectedOptionIds?.includes(
                    option.id
                  ),
                }"
              />
            </div>
            <div
              v-else-if="option.colorHexCode"
              class="ml-2 h-4 w-4"
              :style="`background-color: ${option.colorHexCode}`"
              :class="{
                'border-blue border-2': selectedOptionIds?.includes(option.id),
              }"
            />
            <label
              :for="`filter-mobile-${props.filter.code}-${option.id}`"
              class="ml-1 text-secondary-600"
            >
              {{ getTranslatedProperty(option, "name")
              }}{{ option.count ? " (" + option.count + ")" : "" }}
            </label>
          </li>
        </ul>
      </div>
    </transition>
  </div>
</template>
<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
